import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import LoginScreen from './components/Login';
import AuthCallback from './components/AuthCallback';
import Dashboard from './components/Dashboard';
import Weekly from './components/Weekly';
import Transactions from './components/Transactions';
import Settings from './components/Settings';
import Advisor from './components/Advisor';
import Onboarding from './components/Onboarding';
import { getHousehold, syncPlaid, syncGoogleSheets } from './api/client';
import Privacy from './components/Privacy';

const NAV_ITEMS = [
  { id: "dashboard",    icon: "\uD83D\uDCCA", label: "Overview"     },
  { id: "weekly",       icon: "\uD83D\uDCC5", label: "Weekly"       },
  { id: "transactions", icon: "\uD83D\uDCB3", label: "Transactions" },
  { id: "advisor",      icon: "\uD83E\uDEA9", label: "Advisor"      },
  { id: "settings",     icon: "\u2699\uFE0F",  label: "Settings"    },
];

const AppContent = () => {
  const { user, loading } = useAuth();
  const { COLORS, themeKey } = useTheme();
  const [screen, setScreen]             = useState('dashboard');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [householdName, setHouseholdName]   = useState('');
  const [hhChecked, setHhChecked]           = useState(false);
  const [advisorMessages, setAdvisorMessages] = useState([]);

  // Sync theme-color meta tag with active theme
  useEffect(() => {
    const meta = document.getElementById('theme-color-meta');
    if (meta) meta.setAttribute('content', COLORS.navBg || COLORS.bg);
  }, [themeKey, COLORS]);

  useEffect(() => {
    if (!user) return;
    getHousehold()
      .then(res => {
        const hh = res.data?.data || res.data;
        setHouseholdName(hh?.name || '');
        if (hh && !hh.onboarding_complete) setShowOnboarding(true);
        // Autosync on load — backend will skip if synced within last 30 min
        syncPlaid(false).catch(() => {});
        syncGoogleSheets(false).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setHhChecked(true));
  }, [user]);

  if (loading || (user && !hhChecked)) return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: COLORS.textMuted, fontSize: 16 }}>Loading...</div>
    </div>
  );

  if (window.location.pathname === '/auth/callback') {
    return <AuthCallback onSuccess={() => setScreen('dashboard')} />;
  }
if (window.location.pathname === '/privacy') {
  return <Privacy />;
}

  if (!user) return <LoginScreen />;

  if (showOnboarding) return (
    <Onboarding
      householdName={householdName}
      onComplete={() => setShowOnboarding(false)}
    />
  );

  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg, fontFamily: "'DM Sans', sans-serif", maxWidth: 480, margin: '0 auto' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 50, background: COLORS.bg, borderBottom: `1px solid ${COLORS.border}`, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: COLORS.text, fontSize: 20, fontWeight: 800, letterSpacing: -0.5 }}>{"\uD83D\uDCB0"} Nest Book</span>
        <span style={{ color: COLORS.textMuted, fontSize: 13 }}>Hey, {user.name?.split(' ')[0]}</span>
      </div>
      <div style={{ paddingBottom: 80 }}>
        {screen === 'dashboard'    && <Dashboard    setScreen={setScreen} />}
        {screen === 'weekly'       && <Weekly       />}
        {screen === 'transactions' && <Transactions />}
        {screen === 'advisor'      && <Advisor messages={advisorMessages} setMessages={setAdvisorMessages} />}
        {screen === 'settings'     && <Settings     />}
      </div>
      <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480, background: COLORS.surface, borderTop: `1px solid ${COLORS.border}`, display: 'flex', zIndex: 50 }}>
        {NAV_ITEMS.map(item => (
          <button key={item.id} onClick={() => setScreen(item.id)} style={{ flex: 1, padding: '12px 0', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <span style={{ fontSize: 20 }}>{item.icon}</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: screen === item.id ? COLORS.accent : COLORS.textMuted, fontFamily: 'inherit' }}>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const App = () => (
  <ThemeProvider>
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  </ThemeProvider>
);

export default App;
